import { DENSITY_ORDER, type Density } from './config';

/**
 * Préférence UTILISATEUR : DENSITÉ de la grille (S / M / L), stockée côté client en
 * `localStorage` — même patron que l'ordre de tri par défaut (`default-sort-pref.ts`),
 * le thème et les options de visionneuse (`viewer/viewer-display-pref.ts`).
 *
 * Corrige l'incohérence relevée à l'audit (C2) : `url-state.ts` qualifie la densité de
 * « préférence d'UI locale » — mais elle n'était persistée NI dans l'URL, NI en
 * `localStorage`. Un `useState('M')` la remettait donc à « M » à chaque rechargement,
 * alors que toutes les autres préférences d'affichage survivent.
 *
 * Elle reste HORS de l'URL à dessein : ce n'est pas un « emplacement » adressable (deux
 * personnes qui se partagent un lien doivent voir le même ensemble de photos, pas
 * forcément à la même taille de tuile).
 *
 * Module PUR & testé, tolérant si `localStorage` est indisponible (mode privé strict,
 * quotas) : repli en mémoire.
 */

/** Clé de persistance (namespace `xphoto.*`, comme le tri par défaut). */
export const DENSITY_STORAGE_KEY = 'xphoto.gallery.density';

/** Défaut de fabrique : densité moyenne (valeur historique du `useState`). */
export const DENSITY_FALLBACK: Density = 'M';

/** Repli mémoire quand `localStorage` est inaccessible (jamais d'exception). */
let memoryPref: Density | null = null;

/** Valide une valeur brute contre l'allow-list des densités, ou `null`. */
function parsePref(raw: unknown): Density | null {
  return typeof raw === 'string' && (DENSITY_ORDER as readonly string[]).includes(raw)
    ? (raw as Density)
    : null;
}

/**
 * Lit la préférence persistée, ou `null` si absente/invalide. Le repli MÉMOIRE n'est
 * consulté QUE si `localStorage` est INDISPONIBLE — pas quand il renvoie simplement
 * `null` : vider le stockage doit bien remettre au défaut (même discipline que le tri).
 */
export function readStoredDensity(): Density | null {
  try {
    if (typeof localStorage === 'undefined') return memoryPref;
    return parsePref(localStorage.getItem(DENSITY_STORAGE_KEY));
  } catch {
    return memoryPref;
  }
}

/** Densité effective : persistée sinon le défaut de fabrique (`M`). */
export function getDensity(): Density {
  return readStoredDensity() ?? DENSITY_FALLBACK;
}

/** Persiste la densité (repli mémoire + `localStorage`, silencieux si indisponible). */
export function storeDensity(density: Density): void {
  const valid = parsePref(density);
  if (valid === null) return;
  memoryPref = valid;
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(DENSITY_STORAGE_KEY, valid);
  } catch {
    /* stockage indisponible : la préférence mémoire fait foi pour la session. */
  }
}
